import { sum } from 'lodash-es'
import { useMemo } from 'react'

import {
  CoverOption,
  getAdditionalItemsTotals,
  getPremium,
  getTaxAmount,
  getTotalPremium,
  isCoverActive,
  Quote,
} from '../domain'
import { ProductConfig } from '../state/configuration'
import {
  useAdditionalPremiumBreakdown,
  useMainCovers,
  useOptionalCovers,
  usePromotion,
} from './covers'

export interface QuotePremiums {
  breakdown: {
    main: number
    discount: number
  }
  additionalBreakdown: Record<string, number>
  totals: {
    gross: number
    yearly: number
    discounted: number
    taxAmount: number
  }
  optional: CoverOption[]
}

export const useQuotePremiums = (
  quote: Quote | undefined,
  product: ProductConfig,
): QuotePremiums => {
  const allMainCovers = useMainCovers(quote)
  const allOptionalCovers = useOptionalCovers(quote, product)
  const promotion = usePromotion(quote)
  const allAdditionalItems = useAdditionalPremiumBreakdown(quote)

  return useMemo(() => {
    const mainCovers = allMainCovers.filter(isCoverActive)
    const optionalCovers = allOptionalCovers.filter((it) => it.active)
    const additionalItems = allAdditionalItems.filter((it) => it.active)

    const mainPremium = getPremium(mainCovers)
    const mainYearlyPremium = getTotalPremium(mainCovers)
    const optionalPremium = sum(optionalCovers.map((it) => it.premium))
    const optionalYearlyPremium = sum(optionalCovers.map((it) => it.totalPremium))
    const discountPremium = promotion?.discountAmt ?? 0

    const mainTaxAmount = getTaxAmount(mainCovers)
    const optionalTaxAmount = sum(optionalCovers.map((it) => it.taxAmount))

    const grossTotal = mainPremium + optionalPremium
    const grossYearlyTotal = mainYearlyPremium + optionalYearlyPremium
    const discountedTotal = grossTotal - discountPremium
    const taxAmountTotal = mainTaxAmount + optionalTaxAmount

    const additionalItemsTotals = getAdditionalItemsTotals(additionalItems)

    return {
      breakdown: {
        main: mainPremium,
        discount: discountPremium,
      },
      additionalBreakdown: additionalItemsTotals,
      totals: {
        gross: grossTotal,
        yearly: grossYearlyTotal,
        discounted: discountedTotal,
        taxAmount: taxAmountTotal,
      },
      optional: optionalCovers,
    } satisfies QuotePremiums
  }, [allMainCovers, allOptionalCovers, promotion, allAdditionalItems])
}
